import React, { useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import styles from './MLSamplesDialog.mod.scss';
import clsx from 'clsx';
import { MODEL_TYPES } from '@utils/hooks/machineLearning/params';
import { plotSpectrogram } from '@utils/hooks/machineLearning/util';
import { Button, Checkbox } from '@mui/material';
import { CloseIcon } from '@assets/icons';
import TrashWhite from '@assets/icons/trashWhite';
import CamaraSample from '@assets/icons/camaraSample';
import ImageIcon from '@assets/icons/picIcon';
import CheckIcon from '@assets/icons/checkIcon';


const MLSamplesDialog = ({
  open,
  onOpenChange,
  onLoadSamples,
  onTakeSample,
  loadingSample,
  modelType,
  minSamplesRequired,
  showBackdrop,
  onClose,
  samples,
  mediaWrapper,
  onDeleteSampleImages,
  isCreabots,
  className = "Clase",
}) => {

  const [isDragActive, setIsDragActive] = useState(false);
  const intl = useIntl();
  const inputRef = useRef(null);
  const [selectedImages, setSelectedImages] = useState([]);

  const handleOnTakeSample = (e) => {
    const inputValue = inputRef.current?.value?.trim();
    if (inputValue) {
      onTakeSample(e, inputValue);
      inputRef.current.value = '';
    }
  }


  const footerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragData = useRef({ startX: 0, scrollLeft: 0, threshold: 8 });

  const handleMouseDown = (e) => {

    if (e.target.matches('input[type="checkbox"], button, button *')) return;
    if (e.button !== 0) return;

    setIsDragging(true);
    dragData.current.startX = e.clientX;
    dragData.current.scrollLeft = footerRef.current.scrollLeft;

    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !footerRef.current) return;

    e.preventDefault();
    const deltaX = e.clientX - dragData.current.startX;
    const newScrollLeft = dragData.current.scrollLeft - deltaX;

    footerRef.current.scrollLeft = newScrollLeft;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = handleMouseMove;
    const handleGlobalMouseUp = handleMouseUp;

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging]);



  const handleSelectAllImages = () => {
    if (selectedImages.length === samples.length) {
      setSelectedImages([])
    } else {
      setSelectedImages(samples.map((_, index) => index))
    }
  }

  const handleSelectImage = (index) => {
    if (selectedImages.includes(index)) {
      setSelectedImages(selectedImages.filter(i => i !== index))
    } else {
      setSelectedImages([...selectedImages, index])
    }
  }

  const handleDeleteImages = (indexes) => {
    setSelectedImages([])
    onDeleteSampleImages(indexes)
  }

  const handleLoadBatchSamples = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.multiple = true
    if (modelType === MODEL_TYPES.TEXTS) {
      input.accept = ".txt"
    } else if (modelType === MODEL_TYPES.SOUNDS) {
      input.accept = ".wav,.mp3,.ogg,.m4a"
    } else {
      input.accept = ".jpg,.jpeg,.png"
    }

    input.onchange = async (event) => {
      const target = event.target
      if (!target.files) return

      const files = Array.from(target.files)
      for (const file of files) {
        if (file.type.startsWith("text")) {
          const text = await file.text()
          const words = text.split("\n")
          for (const word of words) {
            await onTakeSample({}, word)
          }
        } else if (file.type.startsWith("image")) {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
              if (typeof reader.result === "string") resolve(reader.result)
              else reject(new Error("Invalid result"))
            }
            reader.onerror = reject
            reader.readAsDataURL(file)
          })
          await onTakeSample({}, dataUrl)
        } else if (file.type.startsWith("audio")) {
          const reader = new FileReader()
          reader.onload = async () => {
            await onTakeSample(
              {},
              undefined,
              reader.result,
            )
          }
          reader.readAsArrayBuffer(file)
        }
      }
    }

    input.click()
  }


  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragActive(false);
    const files = Array.from(event.dataTransfer.files);
    for (const file of files) {
      if (file.type.startsWith("image")) {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === "string") resolve(reader.result);
            else reject(new Error("Invalid result"));
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        await onTakeSample({}, dataUrl);
      }

    }
  };


  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragActive(true);
  };
  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  useEffect(() => {
    if (modelType === MODEL_TYPES.TEXTS && open && !loadingSample) {
      const timeout = setTimeout(() => {
        inputRef.current?.focus()
      }, 0)

      return () => clearTimeout(timeout)
    }

    if (open && modelType === MODEL_TYPES.SOUNDS) {
      const mediaDiv = document.getElementById(mediaWrapper)
      const canvas = mediaDiv?.querySelector("canvas")
      if (canvas) {
        const ctx = canvas.getContext("2d")
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
    }
  }, [modelType, open, loadingSample])

  return (
    <>
      <div className={clsx(styles.mlModalBackdrop, (showBackdrop && open) ? styles.visible : null, (isCreabots) ? styles.isCreabots : null)}></div>
      <div
        className={clsx(styles.samplesDialogModal, (open) ? styles.visible : null, (isCreabots) ? styles.isCreabots : null)}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={isDragActive ? { outline: '3px dashed #735FE3', outlineOffset: '2px', background: '#f5f3ff' } : {}}
      >
        <div className={styles.modalDialog}>
          <div className={`${styles.modalContent}`}>

            <div key="modalHeader" className={styles.modalHeader}>
              <div className={styles.modalHeaderTitle}>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <CloseIcon color='var(--color-placeholder)'/>
                </button>
                <span className={styles.classNameNoWrap}>{typeof className === 'string' ? className.replace(/\n/g, ' ') : className}</span>
              </div>
              <button className={styles.loadSamplesButton} id="load-samples-btn" onClick={onLoadSamples}>
                <CheckIcon />
                {intl.formatMessage({ id: 'machineLearning.samplesDialog.loadSamples' })
                }</button>
            </div>

            <div key="modalBody" className={styles.modalBody}>
              <div id={mediaWrapper} className={`${styles.mediaWrapper} ${(modelType === MODEL_TYPES.TEXTS) ? styles.hidden : ''}`}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  textAlign: 'center',
                  zIndex: 2,
                  pointerEvents: 'none',
                  padding: 0,
                }}>
                  <span style={{
                    display: 'block',
                    width: '100%',
                    background: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    padding: '12px 0',
                    borderRadius: 0,
                    fontWeight: 500,
                    fontSize: '1rem',
                    boxShadow: 'none',
                  }}>{intl.formatMessage({ id: 'machineLearning.samplesDialog.loadSamplesHelp' })}</span>
                </div>
                <canvas></canvas>
                <video playsInline />
                <button
                  className={styles.captureButton}
                  onClick={onTakeSample}
                  id="capture-btn"
                  disabled={loadingSample}
                  style={{ display: modelType === MODEL_TYPES.TEXTS ? 'none' : 'flex' }}
                >
                </button>
              </div>
              <div className={styles.samplesHeader}>
                <div className={styles.samplesCount}>
                  <span className={styles.samplesCountText}>
                    <span>
                      {intl.formatMessage({ id: 'machineLearning.samples' })}: {samples?.length || 0}{" "}
                    </span>
                    <span className={styles.requiredSamples}>
                      ({intl.formatMessage({ id: 'machineLearning.samplesDialog.minSamplesRequired' }, { min: minSamplesRequired.samples })})
                    </span>
                  </span>
                </div>

                {samples?.length > 0 && (
                  selectedImages.length === 0 ? (
                    <button className={styles.selectAll} onClick={handleSelectAllImages}>
                      {intl.formatMessage({ id: 'machineLearning.samplesDialog.selectAll' })}
                    </button>
                  ) : (
                    <button className={styles.selectAll} onClick={() => handleDeleteImages(selectedImages)}>
                      {intl.formatMessage({ id: 'machineLearning.samplesDialog.delete' })}
                    </button>
                  )
                )}
              </div>
              <div>

                {modelType === MODEL_TYPES.TEXTS && (
                  <>
                    <input
                      id="sampleInput"
                      className={styles.sampleInput}
                      ref={inputRef}
                      type="text"
                      placeholder={intl.formatMessage({ id: 'machineLearning.samplesDialog.textSamplesPlaceholder' })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleOnTakeSample(e)
                        }
                      }}
                    />
                    <button
                      className={styles.textCaptureButton}
                      onClick={handleOnTakeSample}
                      disabled={loadingSample}
                    >
                      {intl.formatMessage({ id: 'machineLearning.samplesDialog.takeSample' })}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div
              key="modalFooter"
              className={clsx(styles.modalFooter, isDragging && styles.dragging)}
              ref={footerRef}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              style={{
                cursor: isDragging ? 'default' : 'default',
                userSelect: 'none'
              }}
            >
              {samples?.length === 0 ? (
                <div className={styles.emptyState}>
                  <ImageIcon className={styles.emptyStateIcon} />
                  <span>{intl.formatMessage({ id: 'machineLearning.samplesDialog.noSamples' })}</span>
                </div>
              ) : (
                samples?.map((sample, index) => (
                  <div key={`sample-${index}`}>
                    {/* <button key={`btn-${index}`} onClick={() => handleDeleteImages([index])}>{intl.formatMessage({ id: 'machineLearning.samplesDialog.delete' })}</button> */}
                    <input
                      key={`checkbox-${index}`}
                      type='checkbox'
                      checked={selectedImages.includes(index)}
                      onChange={() => handleSelectImage(index)}
                    />
                    {modelType === MODEL_TYPES.TEXTS && typeof sample === "string" && (
                      <div className={styles.textContainer} key={`text-sample-${index}`}>
                        {sample}
                      </div>
                    )}
                    {/* TODO: take a snapshot for sound's spectrogram */}
                    {/* {modelType === MODEL_TYPES.SOUNDS && typeof sample !== "string" && (
                    <div key={`sound-sample-${index}`} className="">
                      <SpectrogramCanvas key={`canvas-${index}`} index={index} sample={sample} />
                    </div>
                  )} */}
                    {(modelType === MODEL_TYPES.IMAGES ||
                      modelType === MODEL_TYPES.SOUNDS ||
                      modelType === MODEL_TYPES.OBJECTS ||
                      modelType === MODEL_TYPES.POSES ||
                      modelType === MODEL_TYPES.FACES ||
                      modelType === MODEL_TYPES.HANDS) &&
                      typeof sample === "string" && (
                        <div key={`img-container-${index}`} className={styles.imageContainer}>
                          <img
                            key={`img-sample-${index}`}
                            src={sample}
                            alt={`sample-${index}`}
                          />
                          <button
                            key={`delete-btn-${index}`}
                            className={styles.deleteImageButton}
                            onClick={() => handleDeleteImages([index])}
                          >
                            <TrashWhite width={14} height={14} />
                          </button>
                        </div>
                      )}
                  </div>
                ))
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

// function SpectrogramCanvas({ index, sample }) {
//   const canvasRef = useRef(null)

//   useEffect(() => {
//     if (sample) {
//       plotSpectrogram(
//         sample.data,
//         sample.frameSize,
//         sample.frameSize,
//         { pixelsPerFrame: 1 },
//         `spectrogram-${index}`,
//         canvasRef.current,
//       )
//     }
//   }, [sample])

//   return <div id={`spectrogram-${index}`}><canvas ref={canvasRef} className="h-auto w-full rounded-lg object-cover" /></div>
// }

export default MLSamplesDialog;